/**
 * @file リーダーで実際に操作していた時間を測り、読んだ時間 (api/readingSessions.ts) としてサーバへ送る。
 *
 * @what: 数秒おきに「いま数えてよいか」を見て、よければその秒数を開いているページに足す。数えるのは、
 *   タブが見えていて、直近 IDLE_MS の間に操作 (ポインタ・キー・スクロール・タッチ) があるか、音声が
 *   鳴っている間だけである。溜めたぶんは FLUSH_MS ごとと、タブが隠れるとき・ページを離れるとき・
 *   リーダーを閉じるときに、増分としてサーバへ送る (サーバが同じ日の doc に足し込む)。
 *   開いた回数は、最初に秒数が付いた送信に1回として乗せる (開いただけで何もしなかった回は数えない)。
 * @why: 開いていた時間をそのまま数えると、開いたまま離席した時間や別のタブにいた時間が学習になる。
 *   「一定時間以上さわっていた」だけを数える (代表の判断、2026-09-13)。増分で送るのは、同じ日に開き直した
 *   ときや別の端末からの送信を、サーバ側で1つの doc に合流させるためである。
 * @failure: 送信の失敗は console に残すだけで、読む操作は止めない。まなぶ人が未定なら測らない。
 *   日付が変わったら (0時をまたいだら) 溜めたぶんを前の日付で送ってから、新しい日付で数え始める。
 */

import { useEffect, useRef } from "react";
import { todayLocal } from "../api/dates";
import { postReadingSession, type StudyReadingSessionDelta } from "../api/readingSessions";

/** 数える間隔 (ms)。 */
export const READING_SESSION_TICK_MS = 5000;
/** 操作が無くなってから数えるのをやめるまで (ms)。 */
export const READING_SESSION_IDLE_MS = 90_000;
/** 溜めたぶんを送る間隔 (ms)。 */
export const READING_SESSION_FLUSH_MS = 30_000;

/** 操作とみなす出来事 (window で受ける)。 */
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll"] as const;

interface Pending {
  date: string;
  seconds: number;
  pages: Map<number, number>;
  maxPage: number;
}

function emptyPending(date: string): Pending {
  return { date, seconds: 0, pages: new Map(), maxPage: 0 };
}

function toDelta(p: Pending, opens: number): StudyReadingSessionDelta {
  const pages: Record<string, number> = {};
  for (const [page, sec] of p.pages) pages[String(page)] = sec;
  return { date: p.date, active_seconds: p.seconds, opens, pages, max_page: p.maxPage };
}

/**
 * @param page いま開いているページ (連番)。無ければ秒数だけを数える (問で読む面など)。
 * @param audible 音声が鳴っているか (鳴っている間は操作が無くても数える)。
 */
export function useReadingSessionWriter(
  familyId: string,
  personId: string | null,
  sourceId: string,
  page: number | null,
  audible = false,
): void {
  const pageRef = useRef(page);
  pageRef.current = page;
  const audibleRef = useRef(audible);
  audibleRef.current = audible;

  useEffect(() => {
    if (!personId) return;
    const lastActivity = { at: Date.now() };
    let pending = emptyPending(todayLocal());
    // 開いた回数はまだ送っていない (最初に秒数が付いた送信に乗せる)
    let openPending = true;

    const onActivity = () => {
      lastActivity.at = Date.now();
    };
    for (const name of ACTIVITY_EVENTS) window.addEventListener(name, onActivity, { passive: true, capture: true });

    const flush = (keepalive: boolean) => {
      if (pending.seconds <= 0) return;
      const delta = toDelta(pending, openPending ? 1 : 0);
      openPending = false;
      pending = emptyPending(todayLocal());
      postReadingSession(familyId, personId, sourceId, delta, keepalive ? { keepalive: true } : undefined).catch(
        (err: unknown) => {
          console.warn("[study] failed to save the reading session", err);
        },
      );
    };

    const tick = () => {
      const now = Date.now();
      const visible = typeof document === "undefined" || document.visibilityState !== "hidden";
      const active = audibleRef.current || now - lastActivity.at < READING_SESSION_IDLE_MS;
      if (!visible || !active) return;
      const today = todayLocal();
      if (today !== pending.date) flush(false);
      const seconds = READING_SESSION_TICK_MS / 1000;
      pending.seconds += seconds;
      const p = pageRef.current;
      if (p !== null && p >= 1) {
        pending.pages.set(p, (pending.pages.get(p) ?? 0) + seconds);
        if (p > pending.maxPage) pending.maxPage = p;
      }
    };

    const tickTimer = window.setInterval(tick, READING_SESSION_TICK_MS);
    const flushTimer = window.setInterval(() => flush(false), READING_SESSION_FLUSH_MS);
    const onHide = () => {
      if (document.visibilityState === "hidden") flush(true);
    };
    const onPageHide = () => flush(true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.clearInterval(tickTimer);
      window.clearInterval(flushTimer);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
      for (const name of ACTIVITY_EVENTS) window.removeEventListener(name, onActivity, { capture: true });
      flush(true);
    };
  }, [familyId, personId, sourceId]);
}
